import * as React from "react";

import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Input, Textarea } from "@/components/ui/input";
import type { IntakeStep } from "@/lib/queries-intake";
import { latestDay } from "@/lib/record-fields";
import { cn } from "@/lib/utils";

/**
 * Answering the question a card is asking, inside the card (ACL-338).
 *
 * The control is chosen by the field's own type, which the server sends with the step, so this
 * never guesses: a dropdown's choices are the option keys a condition compares, and a Yes/No is
 * two buttons because two buttons on a call beat a select.
 *
 * **One answer at a time, and the server decides what happens next.** Submitting posts the one
 * value and the response is the whole new state of the intake — the route, the next question, the
 * outcome — so the canvas repaints from one reading rather than predicting which question follows.
 * A rep cannot be shown a next question the engine did not actually choose.
 *
 * The draft is local while they type and is thrown away when the step changes, so moving on never
 * carries the last answer into the next card.
 */
export function AnswerInput({
  step,
  pending,
  problem,
  onSubmit,
  onCancel,
}: {
  step: IntakeStep;
  pending: boolean;
  /** What the server refused, shown here by the question rather than as a toast. */
  problem: string | null;
  onSubmit: (value: unknown) => void;
  /** Offered when reopening an answer that is already recorded. */
  onCancel?: () => void;
}) {
  const [draft, setDraft] = React.useState<string>(() => asText(step.value));
  const [picked, setPicked] = React.useState<string[]>(() => asList(step.value));

  // A new question means a new draft: without this the text typed into the last card arrives
  // pre-filled in the next one.
  React.useEffect(() => {
    setDraft(asText(step.value));
    setPicked(asList(step.value));
  }, [step.key, step.value]);

  const locked = pending || !step.may_answer;
  const send = (value: unknown) => {
    if (locked) return;
    onSubmit(value);
  };

  // Yes or no: both answers are one press, which is what a rep on a call needs.
  if (step.type === "boolean") {
    return (
      <Shell problem={problem} onCancel={onCancel}>
        <div className="flex gap-1.5">
          {[
            { value: true, label: "Yes" },
            { value: false, label: "No" },
          ].map((one) => (
            <Button
              key={one.label}
              size="sm"
              variant={step.value === one.value ? "default" : "outline"}
              disabled={locked}
              onClick={() => send(one.value)}
            >
              {one.label}
            </Button>
          ))}
        </div>
      </Shell>
    );
  }

  // One of a list: the options as buttons, so choosing is one press and the choice is visible
  // without opening anything. A long list falls back to a select.
  if (step.type === "dropdown") {
    if (step.options.length > 6) {
      return (
        <Shell problem={problem} onCancel={onCancel}>
          <select
            className="w-full rounded-md border border-border bg-surface px-2 py-1 text-xs"
            disabled={locked}
            value={asText(step.value)}
            onChange={(event) => send(event.target.value || null)}
          >
            <option value="">Pick one…</option>
            {step.options.map((one) => (
              <option key={one.key} value={one.key}>
                {one.label}
              </option>
            ))}
          </select>
        </Shell>
      );
    }
    return (
      <Shell problem={problem} onCancel={onCancel}>
        <div className="flex flex-wrap gap-1.5">
          {step.options.map((one) => (
            <button
              key={one.key}
              type="button"
              disabled={locked}
              onClick={() => send(one.key)}
              className={cn(
                "rounded-full border px-2 py-0.5 text-2xs font-medium transition-colors",
                step.value === one.key
                  ? "border-success-9 bg-success-2 text-success-11"
                  : "border-border bg-surface text-text hover:bg-surface-hover",
                locked && "opacity-60",
              )}
            >
              {one.label}
            </button>
          ))}
        </div>
      </Shell>
    );
  }

  if (step.type === "multi_select") {
    const toggle = (key: string) =>
      setPicked((was) => (was.includes(key) ? was.filter((k) => k !== key) : [...was, key]));
    return (
      <Shell
        problem={problem}
        onCancel={onCancel}
        save={<Save disabled={locked} onClick={() => send(picked)} />}
      >
        <div className="flex flex-wrap gap-1.5">
          {step.options.map((one) => (
            <button
              key={one.key}
              type="button"
              disabled={locked}
              onClick={() => toggle(one.key)}
              className={cn(
                "rounded-full border px-2 py-0.5 text-2xs font-medium",
                picked.includes(one.key)
                  ? "border-success-9 bg-success-2 text-success-11"
                  : "border-border bg-surface text-text hover:bg-surface-hover",
              )}
            >
              {one.label}
            </button>
          ))}
        </div>
      </Shell>
    );
  }

  // A day picked from the month (ACL-577); an accident or a birthday stops at today. Save sends it, as a
  // typed answer's does, so a slip of the finger does not move the flow on.
  if (step.type === "date") {
    const latest = latestDay({ key: step.field_key ?? "", type: step.type });
    return (
      <Shell
        problem={problem}
        onCancel={onCancel}
        save={<Save disabled={locked} onClick={() => send(draft || null)} />}
      >
        <DatePicker
          value={draft}
          max={latest}
          disabled={locked}
          invalid={Boolean(problem)}
          clearable={!step.required}
          aria-label={step.title}
          className="text-xs"
          onChange={setDraft}
        />
      </Shell>
    );
  }

  if (step.type === "long_text") {
    return (
      <Shell
        problem={problem}
        onCancel={onCancel}
        save={<Save disabled={locked} onClick={() => send(draft || null)} />}
      >
        <Textarea
          rows={3}
          value={draft}
          disabled={locked}
          invalid={Boolean(problem)}
          className="text-xs"
          onChange={(event) => setDraft(event.target.value)}
        />
      </Shell>
    );
  }

  // Everything else is one line, typed: the input's own kind does the keyboard and the checking
  // the browser can do, and the server checks it again regardless.
  // The button is outside the form so it can sit beside the way out, and `form=` keeps it the
  // form's submit — Enter in the field still answers the question.
  const formId = `intake-answer-${step.key}`;
  return (
    <Shell
      problem={problem}
      onCancel={onCancel}
      save={<Save disabled={locked} form={formId} />}
    >
      <form
        id={formId}
        onSubmit={(event) => {
          event.preventDefault();
          send(draft === "" ? null : step.type === "number" ? Number(draft) : draft);
        }}
      >
        <Input
          type={inputKind(step.type)}
          value={draft}
          disabled={locked}
          invalid={Boolean(problem)}
          autoComplete="off"
          className="text-xs"
          onChange={(event) => setDraft(event.target.value)}
        />
      </form>
    </Shell>
  );
}

function Shell({
  children,
  problem,
  onCancel,
  save,
}: {
  children: React.ReactNode;
  problem: string | null;
  onCancel?: () => void;
  /** The Save button, where the control has one: drawn beside the way out, never above it. */
  save?: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      {children}
      {problem ? <p className="text-2xs text-danger-11">{problem}</p> : null}
      {/* Save and the way out are one row, not a column: stacked, they read as two unrelated
          things below the input, and "Leave it as it was" sat under the button as though it were
          a second action of it. `save` is given rather than rendered by the caller so the pair is
          composed in one place. */}
      {save || onCancel ? (
        <div className="flex flex-wrap items-center gap-2">
          {save}
          {onCancel ? (
            <button
              type="button"
              onClick={onCancel}
              className="rounded text-2xs text-text-muted underline-offset-2 hover:underline"
            >
              Leave it as it was
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Save({
  disabled, onClick, form,
}: {
  disabled: boolean;
  onClick?: () => void;
  /** The form this submits, when the button sits outside it. */
  form?: string;
}) {
  return (
    <Button
      type={onClick ? "button" : "submit"}
      form={form}
      size="sm"
      className="h-7"
      disabled={disabled}
      onClick={onClick}
    >
      Save
    </Button>
  );
}

function inputKind(type: string | null): string {
  switch (type) {
    case "number":
      return "number";
    case "datetime":
      return "datetime-local";
    case "email":
      return "email";
    case "phone":
      return "tel";
    default:
      return "text";
  }
}

function asText(value: unknown): string {
  if (value === null || value === undefined || Array.isArray(value)) return "";
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
}

function asList(value: unknown): string[] {
  return Array.isArray(value) ? value.map(String) : [];
}
